import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { config } from '@shared/config';
import { getCorrelationId } from './generateCorrelationId';

interface Meta {
    [key: string]: unknown;
}

const { environment } = config;

const shouldSkip = (skip = false): boolean =>
    skip && (environment === 'production' || environment === 'prod');

const enrich = (prefix: string, meta?: Meta, err?: Error | unknown): Meta => {
    const { status, ...rest } = meta ?? {};

    return {
        prefix,
        side: 'client',
        ...rest,
        event_status: status,
        ...(err instanceof Error
            ? {
                  error_name: err.name,
                  error_message: err.message,
                  stack: err.stack,
              }
            : {}),
    };
};

const withCorrelationId = (write: (correlationId: string) => void): void => {
    void getCorrelationId().then(write);
};

export const clientLogger = (prefix: string) => ({
    info: (msg: string, meta?: Meta, skip?: boolean) => {
        if (typeof document === 'undefined' || shouldSkip(skip)) return;
        withCorrelationId((correlationId) => {
            datadogLogs.logger.info(
                `${prefix}: ${msg}`,
                enrich(prefix, { ...meta, correlationId }),
            );
        });
    },

    warn: (msg: string, meta?: Meta, skip?: boolean) => {
        if (typeof document === 'undefined' || shouldSkip(skip)) return;
        withCorrelationId((correlationId) => {
            datadogLogs.logger.warn(
                `${prefix}: ${msg}`,
                enrich(prefix, { ...meta, correlationId }),
            );
        });
    },

    error: (errOrMsg: Error | string, meta?: Meta, skip?: boolean) => {
        if (typeof document === 'undefined' || shouldSkip(skip)) return;
        withCorrelationId((correlationId) => {
            if (errOrMsg instanceof Error) {
                datadogLogs.logger.error(
                    `${prefix}: ${errOrMsg.message}`,
                    enrich(prefix, { ...meta, correlationId }, errOrMsg),
                );
            } else {
                datadogLogs.logger.error(
                    `${prefix}: ${errOrMsg}`,
                    enrich(prefix, { ...meta, correlationId }),
                );
            }
        });
    },
    action: (file: string, action: string, data?: unknown) => {
        datadogRum.addAction(action, {
            file,
            data,
        });
    },
});

export const logInfo = (msg: string, meta?: Meta, skip?: boolean) => {
    if (typeof document === 'undefined' || shouldSkip(skip)) return;
    withCorrelationId((correlationId) => {
        datadogLogs.logger.info(msg, enrich('', { ...meta, correlationId }));
    });
};

export const logWarn = (msg: string, meta?: Meta, skip?: boolean) => {
    if (typeof document === 'undefined' || shouldSkip(skip)) return;
    withCorrelationId((correlationId) => {
        datadogLogs.logger.warn(msg, enrich('', { ...meta, correlationId }));
    });
};

export const logError = (err: Error | string, meta?: Meta, skip?: boolean) => {
    if (typeof document === 'undefined' || shouldSkip(skip)) return;
    withCorrelationId((correlationId) => {
        if (err instanceof Error) {
            datadogLogs.logger.error(err.message, enrich('', { ...meta, correlationId }, err));
        } else {
            datadogLogs.logger.error(err, enrich('', { ...meta, correlationId }));
        }
    });
};
